import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Check,
  Shield,
  Building2,
  Rocket,
  Zap,
  Crown,
  Mail,
  Phone,
  Fingerprint,
  User as UserIcon,
  Lock,
  ArrowRight,
  ClipboardCheck,
  FileText,
  BarChart3,
  Smartphone,
  Package,
  Sparkles,
  Download,
  Share2,
  MoreVertical,
  Plus,
  Copy,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;

const PLAN_META = {
  start:    { icon: Rocket,     accent: "text-emerald-700", border: "border-emerald-300", bg: "bg-emerald-50/50", cta: "Alege Start" },
  business: { icon: Zap,        accent: "text-amber-700",   border: "border-gold",        bg: "bg-gold-soft/40",  cta: "Alege Business", featured: true },
  pro:      { icon: Crown,      accent: "text-purple-700",  border: "border-purple-300",  bg: "bg-purple-50/50",  cta: "Alege Pro" },
};

const PLAN_FEATURES = {
  start: [
    "1 utilizator (agent)",
    "100 clienți în baza de date",
    "50 PV-uri pe lună",
    "Auto-fetch date firmă din ANAF",
    "PDF Contracte + PV cu semnătură",
    "Portal Client gratuit inclus",
  ],
  business: [
    "5 utilizatori (agenți)",
    "500 clienți în baza de date",
    "PV-uri nelimitate",
    "Fișă de Magazie + Stocuri",
    "Dashboard Analiză agenți",
    "SMS/WhatsApp acces client",
    "Suport prioritar",
  ],
  pro: [
    "Utilizatori nelimitați",
    "Clienți nelimitați",
    "PV-uri nelimitate",
    "Toate funcțiile Business",
    "Logo + ștampilă personalizate",
    "QR Code portal pe autocolante",
    "API + integrări custom",
    "Suport dedicat 24/7",
  ],
};

function StepRow({ n, icon, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="shrink-0 w-7 h-7 rounded-full bg-gold-soft text-gold-deep font-bold flex items-center justify-center text-xs">{n}</div>
      <div className="flex-1 pt-0.5">
        {icon && <span className="inline-flex items-center gap-1.5 mr-1 text-slate-700">{icon}</span>}
        <span>{children}</span>
      </div>
    </div>
  );
}

export default function Subscribe() {
  const nav = useNavigate();
  const [plans, setPlans] = useState({});
  const [selectedPlan, setSelectedPlan] = useState("business");
  const [form, setForm] = useState({
    denumire: "",
    cui: "",
    admin_name: "",
    email: "",
    telefon: "",
    password: "",
  });
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("plans"); // "plans" | "form"
  const [installPrompt, setInstallPrompt] = useState(null);
  const [platform, setPlatform] = useState("other"); // "ios" | "android" | "other"
  const [isStandalone, setIsStandalone] = useState(false);
  const [showInstallGuide, setShowInstallGuide] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/tenants/plans`).then((r) => setPlans(r.data.plans || {})).catch(() => {});
    const onBip = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", onBip);
    const ua = navigator.userAgent || "";
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /Android/i.test(ua);
    setPlatform(isIos ? "ios" : isAndroid ? "android" : "other");
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    setIsStandalone(!!standalone);
    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  const installApp = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      setInstallPrompt(null);
      if (choice?.outcome === "accepted") {
        toast.success("Aplicația a fost instalată pe telefon.");
      }
      return;
    }
    setShowInstallGuide(true);
  };

  const pickPlan = (code) => {
    setSelectedPlan(code);
    setStep("form");
    setTimeout(() => document.getElementById("register-form")?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.denumire || !form.cui || !form.email || !form.password || !form.admin_name) {
      toast.error("Completează toate câmpurile obligatorii");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Parola trebuie să aibă minim 6 caractere");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/tenants/register`, {
        ...form,
        plan: "trial", // Everyone starts on trial regardless of selected plan
      });
      localStorage.setItem("token", data.token);
      toast.success(`Bine ai venit, ${data.user.name}! Trial 14 zile activat.`);
      setTimeout(() => { window.location.href = "/"; }, 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Înregistrare eșuată");
    } finally {
      setBusy(false);
    }
  };

  const selectedMeta = PLAN_META[selectedPlan] || PLAN_META.business;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100">
      <Toaster richColors position="top-center" />

      {/* Nav */}
      <nav className="border-b border-white/5 bg-slate-950/80 backdrop-blur sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-gradient-to-br from-gold to-gold-deep flex items-center justify-center">
              <Building2 className="w-6 h-6 text-slate-900" />
            </div>
            <div>
              <p className="font-display font-black text-lg leading-tight">IMAGGIO DDD</p>
              <p className="text-[10px] uppercase tracking-widest text-slate-400">CRM pentru firme DDD</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login" className="text-sm text-slate-300 hover:text-gold-deep transition">Am cont</Link>
            <a href="#pricing" className="hidden sm:inline text-sm text-slate-300 hover:text-gold-deep transition ml-4">Prețuri</a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 py-16 sm:py-24 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold-soft/20 border border-gold/40 mb-6">
          <Sparkles className="w-3.5 h-3.5 text-gold" />
          <span className="text-xs font-semibold uppercase tracking-widest text-gold">Trial gratuit 14 zile · fără card</span>
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white leading-[1.1]">
          Aplicație <span className="text-gold-deep">CRM</span> pentru<br />firme <span className="italic text-gold">Dezinsecție · Deratizare · Dezinfecție</span>
        </h1>
        <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl mx-auto">
          Gestionează clienți, contracte, procese-verbale și stocuri de substanțe biocide într-un singur loc.
          Portal separat pentru clienți, generare automată PDF-uri, izolare completă a datelor firmei tale.
        </p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Button
            size="lg"
            onClick={() => pickPlan("business")}
            className="h-12 px-8 btn-gold text-base font-semibold"
            data-testid="hero-cta-start"
          >
            <Rocket className="w-5 h-5 mr-2" /> Începe trial gratuit 14 zile
          </Button>
          <Button
            size="lg"
            onClick={installApp}
            variant="outline"
            className="h-12 px-8 border-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200"
            data-testid="hero-install-app"
          >
            <Smartphone className="w-5 h-5 mr-2" /> Descarcă aplicația pe telefon
          </Button>
          <Button
            size="lg"
            variant="outline"
            asChild
            className="h-12 px-8 border-white/20 bg-white/5 hover:bg-white/10 text-white"
          >
            <a href="#pricing">Vezi prețurile</a>
          </Button>
        </div>

        {isStandalone && (
          <p className="mt-4 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-md py-2 px-3 inline-block">
            ✓ Aplicația e deja instalată pe acest telefon
          </p>
        )}

        {/* Feature strip */}
        <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
          {[
            { icon: FileText, label: "Contracte & PV" },
            { icon: Package, label: "Stocuri + Fișă" },
            { icon: BarChart3, label: "Analiză agenți" },
            { icon: Smartphone, label: "Portal Client PWA" },
          ].map((f, i) => (
            <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-4 text-center">
              <f.icon className="w-6 h-6 text-gold mx-auto mb-2" />
              <p className="text-xs font-semibold text-slate-200">{f.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Instalare aplicație — secțiune dedicată */}
      <section className="max-w-5xl mx-auto px-4 py-16 border-t border-white/5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/40 mb-4">
              <Smartphone className="w-3.5 h-3.5 text-emerald-300" />
              <span className="text-xs font-semibold uppercase tracking-widest text-emerald-300">Aplicație mobilă</span>
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-white leading-tight">
              Descarcă aplicația<br />pe <span className="text-gold-deep">telefon</span> sau <span className="text-gold-deep">tabletă</span>
            </h2>
            <p className="mt-4 text-slate-400 text-sm sm:text-base">
              Fără magazin de aplicații, fără cont Google/Apple. Un singur click →
              iconița IMAGGIO DDD apare pe ecranul principal și pornește ca o aplicație normală.
            </p>
            <ul className="mt-6 space-y-2.5">
              {[
                "Funcționează pe Android (Chrome/Edge) și iPhone (Safari)",
                "Ocupă doar ~2 MB · fără update-uri manuale",
                "Notificări și instalare rapidă din browserul telefonului",
                "PDF-uri și contracte disponibile și offline",
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                  <Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-400" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button
                onClick={installApp}
                className="h-12 px-6 btn-gold text-base font-semibold"
                data-testid="section-install-app"
              >
                <Download className="w-5 h-5 mr-2" /> Instalează aplicația
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowInstallGuide(true)}
                className="h-12 px-6 border-white/20 bg-white/5 hover:bg-white/10 text-white"
              >
                <Smartphone className="w-5 h-5 mr-2" /> Vezi pași de instalare
              </Button>
            </div>
          </div>

          <div className="relative">
            {/* Mockup telefon */}
            <div className="relative mx-auto w-64 h-[440px] bg-slate-900 rounded-[2.5rem] border-8 border-slate-800 shadow-2xl overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-slate-800 rounded-b-2xl z-10" />
              <div className="h-full bg-gradient-to-b from-gold-soft via-white to-white p-4 flex flex-col items-center justify-center gap-3">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-gold to-gold-deep flex items-center justify-center shadow-lg">
                  <Building2 className="w-10 h-10 text-slate-900" />
                </div>
                <p className="font-display font-black text-slate-900 text-lg text-center leading-tight">IMAGGIO<br />DDD</p>
                <div className="w-full mt-2 space-y-2">
                  {[
                    { label: "Clienți", val: "1,539" },
                    { label: "Contracte", val: "234" },
                    { label: "PV-uri luna", val: "48" },
                  ].map((s, i) => (
                    <div key={i} className="flex justify-between bg-white/70 rounded-md px-3 py-2 text-xs">
                      <span className="text-slate-600 font-medium">{s.label}</span>
                      <span className="text-gold-deep font-black">{s.val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full bg-emerald-500/20 blur-2xl" />
            <div className="absolute -bottom-4 -left-4 w-20 h-20 rounded-full bg-gold/20 blur-2xl" />
          </div>
        </div>
      </section>

      {/* Dialog instrucțiuni instalare — reused pentru mai multe butoane */}
      <Dialog open={showInstallGuide} onOpenChange={setShowInstallGuide}>
        <DialogContent className="max-w-md bg-white text-slate-900">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Smartphone className="w-6 h-6 text-gold-deep" />
              Instalează pe telefon
            </DialogTitle>
            <DialogDescription>
              Fără magazin de aplicații. Iconița apare direct pe ecranul principal.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 text-sm">
            {platform === "ios" ? (
              <div className="space-y-3">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">iPhone / iPad (Safari)</div>
                <StepRow n={1} icon={<Share2 className="w-4 h-4" />}>
                  Apasă butonul <b>Share</b> din bara de jos în Safari.
                </StepRow>
                <StepRow n={2} icon={<Plus className="w-4 h-4" />}>
                  Alege <b>„Adaugă la ecranul de start"</b>.
                </StepRow>
                <StepRow n={3}>
                  Confirmă cu <b>„Adaugă"</b> — iconița IMAGGIO DDD va apărea pe ecranul principal.
                </StepRow>
                <div className="text-[11px] italic text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  Notă: iOS acceptă instalare doar din <b>Safari</b>. Dacă ești în Chrome, copiază linkul și deschide-l în Safari.
                </div>
              </div>
            ) : platform === "android" ? (
              <div className="space-y-3">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Android (Chrome / Edge / Samsung Internet)</div>
                <StepRow n={1} icon={<MoreVertical className="w-4 h-4" />}>
                  Apasă meniul cu <b>3 puncte</b> (dreapta sus în browser).
                </StepRow>
                <StepRow n={2}>
                  Alege <b>„Instalare aplicație"</b> sau <b>„Adaugă pe ecranul de start"</b>.
                </StepRow>
                <StepRow n={3}>
                  Confirmă cu <b>„Instalare"</b>.
                </StepRow>
              </div>
            ) : (
              <div className="space-y-3">
                <StepRow n={1}>Deschide acest link direct de pe telefonul tău.</StepRow>
                <StepRow n={2}>În browserul telefonului, apasă meniul (3 puncte pe Android, Share pe iPhone).</StepRow>
                <StepRow n={3}>Alege <b>„Adaugă pe ecranul de start"</b> sau <b>„Instalare aplicație"</b>.</StepRow>
              </div>
            )}

            <div className="pt-3 border-t border-border">
              <p className="text-xs text-slate-600 mb-1">Link către aplicație:</p>
              <div className="flex gap-2">
                <code className="flex-1 text-xs bg-slate-100 px-2 py-2 rounded truncate">{window.location.origin}</code>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard?.writeText(window.location.origin);
                    toast.success("Link copiat");
                  }}
                >
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copiază
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pricing */}
      <section id="pricing" className="max-w-6xl mx-auto px-4 py-16 sm:py-20">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-widest text-gold font-semibold">Prețuri simple</p>
          <h2 className="font-display font-black text-3xl sm:text-4xl mt-2">Alege planul potrivit firmei tale</h2>
          <p className="mt-3 text-slate-400 text-sm">Toate planurile încep cu <b className="text-gold">14 zile trial gratuit</b>. Fără card la înscriere.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {["start", "business", "pro"].map((code) => {
            const meta = PLAN_META[code];
            const info = plans[code];
            const Icon = meta.icon;
            const features = PLAN_FEATURES[code];
            const isFeatured = meta.featured;
            return (
              <div
                key={code}
                className={`relative rounded-xl border-2 ${isFeatured ? "border-gold shadow-2xl shadow-gold/20 scale-[1.03]" : "border-white/10"} bg-slate-900/60 backdrop-blur p-6 flex flex-col`}
                data-testid={`plan-card-${code}`}
              >
                {isFeatured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-gold text-slate-900 text-[10px] font-black uppercase tracking-widest">
                    Cel mai popular
                  </div>
                )}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-11 h-11 rounded-md ${meta.bg} border ${meta.border} flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 ${meta.accent}`} />
                  </div>
                  <div>
                    <p className="font-display font-black text-2xl text-white">{info?.label || code}</p>
                    <p className="text-xs uppercase tracking-widest text-slate-500">Plan {code}</p>
                  </div>
                </div>
                <div className="mb-5">
                  <span className="font-display font-black text-4xl text-white">{info?.price_lei ?? "—"}</span>
                  <span className="text-sm text-slate-400 ml-1">lei / lună</span>
                </div>
                <ul className="space-y-2.5 mb-6 flex-1">
                  {features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${meta.accent}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => pickPlan(code)}
                  className={`w-full h-11 font-semibold ${isFeatured ? "btn-gold" : "bg-white/10 hover:bg-white/20 text-white border border-white/20"}`}
                  data-testid={`plan-cta-${code}`}
                >
                  {meta.cta} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-slate-500 mt-8">
          Toate prețurile sunt fără TVA. Anulare oricând din setări. Datele tale rămân stocate 90 de zile după anulare.
        </p>
      </section>

      {/* Register form */}
      <section id="register-form" className="max-w-2xl mx-auto px-4 py-16">
        <div className="bg-white text-slate-900 rounded-xl shadow-2xl border border-gold/30 p-6 sm:p-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 border border-emerald-300 mb-3">
              <Shield className="w-3.5 h-3.5 text-emerald-700" />
              <span className="text-xs font-semibold uppercase tracking-widest text-emerald-800">Trial gratuit 14 zile</span>
            </div>
            <h2 className="font-display font-black text-3xl">Creează contul firmei tale</h2>
            <p className="text-sm text-slate-500 mt-2">
              Ai selectat planul{" "}
              <b className={selectedMeta.accent}>{plans[selectedPlan]?.label || selectedPlan.toUpperCase()}</b>.
              Poți schimba planul oricând din setări după înscriere.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {/* Company details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label className="label-eyebrow flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5" /> Denumire firmă *
                </Label>
                <Input
                  value={form.denumire}
                  onChange={(e) => setForm({ ...form, denumire: e.target.value })}
                  placeholder="ex: DDD Servicii SRL"
                  className="mt-2 h-11"
                  required
                  data-testid="register-denumire"
                />
              </div>
              <div>
                <Label className="label-eyebrow flex items-center gap-1.5">
                  <Fingerprint className="w-3.5 h-3.5" /> CUI firmă *
                </Label>
                <Input
                  value={form.cui}
                  onChange={(e) => setForm({ ...form, cui: e.target.value.toUpperCase() })}
                  placeholder="ex: 12345678 sau RO12345678"
                  className="mt-2 h-11 font-mono"
                  required
                  data-testid="register-cui"
                />
              </div>
              <div>
                <Label className="label-eyebrow flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5" /> Telefon firmă
                </Label>
                <Input
                  type="tel"
                  value={form.telefon}
                  onChange={(e) => setForm({ ...form, telefon: e.target.value })}
                  placeholder="ex: 0721234567"
                  className="mt-2 h-11"
                  data-testid="register-telefon"
                />
              </div>
            </div>

            <div className="border-t border-border pt-4 mt-2">
              <p className="label-eyebrow mb-3">Cont administrator</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label className="label-eyebrow flex items-center gap-1.5">
                    <UserIcon className="w-3.5 h-3.5" /> Nume complet *
                  </Label>
                  <Input
                    value={form.admin_name}
                    onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                    placeholder="Ion Popescu"
                    className="mt-2 h-11"
                    required
                    data-testid="register-admin-name"
                  />
                </div>
                <div>
                  <Label className="label-eyebrow flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" /> Email *
                  </Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="admin@firmata.ro"
                    className="mt-2 h-11"
                    required
                    data-testid="register-email"
                  />
                </div>
                <div>
                  <Label className="label-eyebrow flex items-center gap-1.5">
                    <Lock className="w-3.5 h-3.5" /> Parolă *
                  </Label>
                  <Input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="minim 6 caractere"
                    className="mt-2 h-11"
                    minLength={6}
                    required
                    data-testid="register-password"
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={busy}
              className="w-full h-12 btn-gold text-base font-semibold"
              data-testid="register-submit"
            >
              {busy ? "Se creează contul..." : (
                <>
                  <Rocket className="w-5 h-5 mr-2" />
                  Activează Trial Gratuit 14 zile
                </>
              )}
            </Button>

            <p className="text-[11px] text-center text-slate-500 pt-2">
              Prin apăsarea butonului, ești de acord cu Termenii și Condițiile.<br />
              Fără card acum · anulare oricând · datele tale rămân 100% izolate.
            </p>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 bg-slate-950 py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <p>© IMAGGIO 2020 TIM SRL · CRM pentru firme DDD</p>
          <div className="flex items-center gap-4">
            <Link to="/login" className="hover:text-gold-deep transition">Autentificare</Link>
            <Link to="/client-portal" className="hover:text-gold-deep transition">Portal Client</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
